import {
    CONSENT_CHANGE_TYPES,
    TEXT_CUSTOMIZATION_CHANGE_KEYS,
} from '/b2b/common/components/AuditEventLog/components/AuditEvent/constants';
import { countryNameLookup, territoryOptions } from '/b2b/common/helpers/Address';
import { isPlainObject, isString } from 'lodash';

import { US_BANNER_VARIANTS } from '/b2b/consent/variantMapping';
import { translate } from '/b2b/common/helpers/i18n';

// Maps jurisdiction codes as stored in analyticsOnByDefault (e.g. 'us-ca') to
// their display names (e.g. 'California'). Territory keys look like 'US_CA'.
export const JURISDICTION_LABELS = Object.values(territoryOptions).reduce((labels, territories) => {
    Object.entries(territories).forEach(([key, { name }]) => {
        labels[key.toLowerCase().replace(/_/g, '-')] = name;
    });
    return labels;
}, {});

export const prettyJurisdiction = code => {
    const key = String(code).toLowerCase();
    // Country-level codes like 'us' have no hyphen and aren't in the territory map.
    return JURISDICTION_LABELS[key] || countryNameLookup(key) || code;
};

const VARIANT_MAPPING_KEY = 'variantMapping';
const LEGACY_VARIANT_MAPPING_KEY = 'usVariantMapping';
const BY_JURISDICTION_KEY = 'byJurisdiction';
const NON_JURISDICTION_KEYS = ['defaults', 'behavior', BY_JURISDICTION_KEY];

// The mapping key a change targets, or undefined for any other path.
const usVariantKey = path => {
    const p = String(path ?? '').replace(/^configuration\./, '');
    const prefix = [VARIANT_MAPPING_KEY, LEGACY_VARIANT_MAPPING_KEY]
        .map(key => `${key}.`)
        .find(candidate => p.startsWith(candidate));
    if (!prefix) {
        return undefined;
    }
    const key = p.slice(prefix.length);
    return key.startsWith(`${BY_JURISDICTION_KEY}.`)
        ? key.slice(BY_JURISDICTION_KEY.length + 1)
        : key;
};

const prettyUsVariant = value => {
    switch (value) {
        case US_BANNER_VARIANTS.OPT_OUT:
            return translate('activityLog.usVariant.optOut');
        case US_BANNER_VARIANTS.OPT_IN:
            return translate('activityLog.usVariant.optIn');
        case null:
        case undefined:
            return translate('activityLog.usVariant.default');
        default:
            return undefined;
    }
};

export const prettyScope = path => {
    const usKey = usVariantKey(path);
    if (usKey && !NON_JURISDICTION_KEYS.includes(usKey)) {
        return prettyJurisdiction(usKey);
    }

    return undefined;
};

export const prettyEventValue = (value, path) => {
    const usKey = usVariantKey(path);
    const usVariant = usKey && usKey !== 'behavior' ? prettyUsVariant(value) : undefined;
    if (usVariant) {
        return usVariant;
    }
    if (value == null) {
        return translate('common.empty');
    } // covers null & undefined
    if (typeof value === 'boolean') {
        return value ? translate('common.true') : translate('common.false');
    }
    if (Array.isArray(value)) {
        switch (path) {
            case 'domains':
                return value.length === 0 ? translate('common.empty') : value.join(', ');
            case 'organizations':
                return value.length === 0
                    ? translate('common.empty')
                    : value
                          .map(org =>
                              isPlainObject(org) ? org.name || org.id || JSON.stringify(org) : org
                          )
                          .join(', ');
            case 'configuration.additionalLinks':
                return value.length === 0
                    ? translate('common.empty')
                    : value
                          .map(link =>
                              isPlainObject(link)
                                  ? link.label || link.url || JSON.stringify(link)
                                  : link
                          )
                          .join(', ');
            case 'analyticsOnByDefault':
            case 'configuration.analyticsOnByDefault':
                return value.length === 0
                    ? translate('common.empty')
                    : value.map(prettyJurisdiction).join(', ');
            case 'configuration.doNotSellCategories':
                return value.length === 0 ? translate('common.empty') : value.join(', ');
            default:
                return `${value.length} ${translate('common.item', { smart_count: value.length })}`;
        }
    }
    if (typeof value === 'object') {
        // Localized value maps like { en: 'Hello', fr: 'Bonjour' }: prefer the
        // single value, then English, then any string, and fall back to JSON.
        const keys = Object.keys(value);
        if (keys.length === 1 && typeof value[keys[0]] === 'string') {
            return value[keys[0]];
        }
        const firstStrKey = keys.find(k => typeof value[k] === 'string');
        if (firstStrKey) {
            return value.en || value[firstStrKey];
        }
        return JSON.stringify(value);
    }
    return String(value);
};

const labelForTextCustomization = section => {
    switch (section) {
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.MESSAGING:
            return translate('activityLog.changes.bannerText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.BUTTONS:
            return translate('activityLog.changes.buttonText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.ALERTS:
            return translate('activityLog.changes.alertText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.CATEGORIES:
            return translate('activityLog.changes.categoriesText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.DRAWER:
            return translate('activityLog.changes.drawerText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.DO_NOT_SELL:
            return translate('activityLog.changes.doNotSellText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.LABELS:
            return translate('activityLog.changes.labelsText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.NOTIFICATIONS:
            return translate('activityLog.changes.notificationsText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.MODALS:
            return translate('activityLog.changes.modalsText');
        case TEXT_CUSTOMIZATION_CHANGE_KEYS.FORMS:
            return translate('activityLog.changes.formsText');
        default:
            return translate('common.text');
    }
};

export const prettyPath = (path, changeType) => {
    if (usVariantKey(path)) {
        return translate('views.ConsentSettings.fields.usStateBanner.title');
    }
    let p = String(path ?? translate('common.empty')).replace(/^configuration\./, '');
    if (changeType === CONSENT_CHANGE_TYPES.TEXT_CUSTOMIZATION) {
        const parts = p.split('.');
        if (parts.includes('translations') && parts.length > 1) {
            const section = parts[parts.indexOf('translations') + 1];
            return (
                `${labelForTextCustomization(section)} ${parts[parts.length - 1].toUpperCase()}` ||
                translate('common.text')
            );
        }
    }
    const last = p.split('.').pop();
    const spaced = last.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.replace(/\b\w/g, l => l.toUpperCase());
};

export const headingFor = ({ changeType }) => {
    switch (changeType) {
        case CONSENT_CHANGE_TYPES.TEXT_CUSTOMIZATION:
            return translate('activityLog.textChange');
        case CONSENT_CHANGE_TYPES.STYLE:
            return translate('activityLog.styleChange');
        case CONSENT_CHANGE_TYPES.IAB:
            return translate('activityLog.iabSettingChange');
        case CONSENT_CHANGE_TYPES.RULE:
            return translate('activityLog.ruleChange');
        default:
            return translate('activityLog.settingChange');
    }
};

const safeParse = value => {
    if (isString(value)) {
        try {
            return JSON.parse(value);
        } catch {
            return undefined;
        }
    }
    return isPlainObject(value) ? value : undefined;
};

export const parseAdditional = additional => {
    const obj = safeParse(additional) ?? {};
    if (isString(obj.metadata)) {
        obj.metadata = safeParse(obj.metadata) ?? {};
    }
    return obj;
};
